'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Copy, Edit2, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useDeleteTemplate,
  useDuplicateTemplate,
  usePlaybookTemplates,
} from '@/features/playbooks/api/use-playbook-templates';
import { Nis2WorkflowTemplateMatrix } from '@/features/playbooks/components/Nis2WorkflowTemplateMatrix';
import { TemplateCard } from '@/features/playbooks/components/TemplateCard';
import { WorkflowSectionTabs } from '@/features/playbooks/components/WorkflowSectionTabs';
import type { PlaybookTemplateListItem } from '@/features/playbooks/types';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/tasks/playbooks/templates/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.workflowTemplates'),
  }),
  component: TemplateListPage,
});

function TemplateListPage() {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [deleteTarget, setDeleteTarget] = useState<PlaybookTemplateListItem | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<PlaybookTemplateListItem | null>(null);

  const { templates, isLoading } = usePlaybookTemplates(
    domainFilter === 'all' ? undefined : domainFilter
  );
  const { deleteTemplate, isDeleting } = useDeleteTemplate();
  const { duplicateTemplate, isDuplicating } = useDuplicateTemplate();

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.id);
      toast.success(t('tasks:toast.hasBeenDeleted', { deleteTarget: deleteTarget.name }));
      setDeleteTarget(null);
    } catch {
      toast.error(t('tasks:toast.thisTemplateMayHaveActiveActivations'));
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateTarget) return;
    try {
      const result = await duplicateTemplate({ templateId: duplicateTarget.id });
      toast.success(t('tasks:toast.hasBeenCreated', { result: result.name }));
      setDuplicateTarget(null);
      void navigate({
        to: '/tasks/playbooks/templates/$templateId',
        params: { templateId: result.id },
      });
    } catch {
      toast.error(t('tasks:toast.somethingWentWrongDuplicatingTheTemplate'));
    }
  };

  return (
    <div className="space-y-6">
      <WorkflowSectionTabs activeTab="templates" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('templates.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('templates.description')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={domainFilter} onValueChange={setDomainFilter}>
            <SelectTrigger className="w-[140px]" data-testid="domain-filter">
              <SelectValue placeholder={t('templates.filterDomain')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('templates.allDomains')}</SelectItem>
              <SelectItem value="ghg">{t('templates.domainGhg')}</SelectItem>
              <SelectItem value="esg">{t('templates.domainEsg')}</SelectItem>
              <SelectItem value="nis2">{t('templates.domainNis2')}</SelectItem>
              <SelectItem value="custom">{t('templates.domainCustom')}</SelectItem>
            </SelectContent>
          </Select>
          <Button asChild data-testid="create-template-btn">
            <Link to="/tasks/playbooks/templates/new">
              <Plus className="mr-2 h-4 w-4" />
              {t('templates.createTemplate')}
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <p className="text-muted-foreground">{t('templates.noTemplates')}</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((tmpl) => (
            <div
              key={tmpl.id}
              className={cn(
                'group relative',
                tmpl.domain === 'nis2' && tmpl.isSystem && 'md:col-span-2 lg:col-span-3'
              )}
            >
              <TemplateCard template={tmpl} showSystemBadge>
                {tmpl.domain === 'nis2' && tmpl.isSystem ? <Nis2WorkflowTemplateMatrix /> : null}
              </TemplateCard>
              <div className="absolute top-3 right-3 z-10 flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  title={t('templates.duplicate')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDuplicateTarget(tmpl);
                  }}
                  data-testid={`duplicate-template-${tmpl.code}`}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                {!tmpl.isSystem && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      title={t('templates.edit')}
                      onClick={(e) => {
                        e.stopPropagation();
                        void navigate({
                          to: '/tasks/playbooks/templates/$templateId',
                          params: { templateId: tmpl.id },
                        });
                      }}
                      data-testid={`edit-template-${tmpl.code}`}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                      title={t('templates.delete')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(tmpl);
                      }}
                      data-testid={`delete-template-${tmpl.code}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('templates.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('templates.deleteConfirm', { name: deleteTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('templates.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? t('templates.deleting') : t('templates.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate confirmation dialog */}
      <AlertDialog
        open={!!duplicateTarget}
        onOpenChange={(open) => {
          if (!open) setDuplicateTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('templates.duplicateTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('templates.duplicateConfirm', { name: duplicateTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('templates.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDuplicate()} disabled={isDuplicating}>
              {isDuplicating ? t('templates.duplicating') : t('templates.duplicate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
